import { useMemo } from "react";
import { useDateFnsFormattersWithLocale, type DashboardCardProps } from "@tellescope/ui";
import { isToday } from "date-fns/isToday";
import { isTomorrow } from "date-fns/isTomorrow";
import { isThisWeek } from "date-fns/isThisWeek";
import { useAuth, useTellescopeInfiniteQuery, useUsersMap } from "@tellescope/tanstack-query";
import { createIdMap } from "@tellescope/utils";

/**
 * Custom hook to fetch and transform inbox threads for dashboard cards
 */
export function useDashboardInboxData(limit: number = 10) {
  const { format } = useDateFnsFormattersWithLocale();
  const { currentUser } = useAuth();
  const inboxThreadsQuery = useTellescopeInfiniteQuery({
    model: "inbox_threads",
    method: "load_threads",
    request: {
      autobuild: true,
      limit: limit,
      mdbFilter: {
        assignedTo: {
          $elemMatch: {
            $in: [currentUser?.id],
          },
        },
        inboxStatus: "New",
      },
    },
    getNextPageParam: (lastPage) => lastPage.threads.at(-1)?.timestamp,
    initialPageParam: undefined,
    paginateRequest: (lastTimestamp, request) => ({
      ...request,
      lastTimestamp,
    }),
    select: (data) => data.pages.map(({ threads }) => threads).flat(),
  });
  const usersById = useUsersMap();
  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {},
    enabled: !!currentUser.id,
    select: (data) => {
      const endusers = data.pages.flat();
      return {
        endusers,
        recordsById: createIdMap(endusers),
      };
    },
  });

  // Transform inbox threads to dashboard card format
  const messageCards: DashboardCardProps[] = useMemo(() => {
    const threads = inboxThreadsQuery.data || [];

    // Debug logging
    console.log("Dashboard hook - inbox threads:", threads.length);
    if (threads.length > 0) {
      console.log("First thread:", threads[0]);
    }

    return threads.map((thread) => {
      // Format the due date based on timestamp
      let dueDate = "Unknown";
      if (thread.timestamp) {
        const date = new Date(thread.timestamp);
        if (isToday(date)) {
          dueDate = "Today";
        } else if (isTomorrow(date)) {
          dueDate = "Tomorrow";
        } else if (isThisWeek(date)) {
          dueDate = "This Week";
        } else {
          dueDate = format(date, "MMM d");
        }
      }

      // Map inbox status to dashboard status
      let status: "default" | "secondary" | "destructive" | "outline" = "default";
      let statusLabel = thread.inboxStatus || "Unknown";

      switch (thread.inboxStatus) {
        case "New":
          status = "destructive";
          break;
        case "Open":
          status = "secondary";
          break;
        case "Closed":
          status = "outline";
          break;
        default:
          status = "default";
      }

      // Get assigned users for avatars
      let assignees: Array<{ src?: string; alt?: string }> | null = null;
      if (thread.assignedTo && Array.isArray(thread.assignedTo)) {
        assignees = thread.assignedTo
          .map((userId: string) => {
            const user = usersById[userId];
            return {
              src: `https://i.pravatar.cc/150?u=${userId}`,
              alt: user?.fname && user?.lname ? `${user.fname} ${user.lname}` : user?.email || "User",
            };
          })
          .filter(Boolean);
      }

      // Create a descriptive title based on type and enduser
      let title = thread.title || `${thread.type} Message`;

      // Add enduser context if available
      if (thread.enduserIds && thread.enduserIds.length > 0) {
        const firstEnduserId = thread.enduserIds[0];
        const enduser = firstEnduserId ? endusersQuery.data?.recordsById[firstEnduserId] : undefined;
        if (enduser) {
          const enduserName =
            enduser.fname && enduser.lname ? `${enduser.fname} ${enduser.lname}` : enduser.email || "Patient";
          title = `${thread.type}: ${enduserName}`;
        }
      }

      return {
        title,
        description: thread.preview || "No preview available",
        dueDate,
        assignees,
        statusLabel,
        status,
        // Add timestamp for display in footer
        timestamp: thread.timestamp || thread.outboundTimestamp,
      };
    });
  }, [inboxThreadsQuery.data, format, usersById, endusersQuery.data?.recordsById]);

  // Get the original threads for use with InboxThreadListDefaultItem
  const threads = useMemo(() => {
    return inboxThreadsQuery.data || [];
  }, [inboxThreadsQuery.data]);

  return {
    messageCards,
    threads, // Add original thread objects
    isLoading: inboxThreadsQuery.isLoading,
    error: inboxThreadsQuery.error,
    hasLoadedInitial: !inboxThreadsQuery.isLoading,
    // Debug info
    totalThreads: (inboxThreadsQuery.data || []).length,
    threadsSliceState: {
      hasLoadedInitial: !inboxThreadsQuery.isLoading,
      isLoading: inboxThreadsQuery.isLoading,
      error: inboxThreadsQuery.error,
    },
  };
}
